<template>
	<div>
		<v-row>
			<v-col cols="12" md="3">
				<v-text-field
					v-model="searchfield"
					label="Cerca prodotto, SKU o codice fornitore"
					outlined
					dense
					hide-details
					clearable
					append-icon="mdi-magnify"
					@keyup.enter="cerca"
					@click:append="cerca"
					@click:clear="pulisci"
				/>
			</v-col>
			<v-col cols="12" md="3">
				<v-autocomplete v-model="filters.supplier" :items="supplierItems" label="Fornitore" outlined dense hide-details clearable />
			</v-col>
			<v-col cols="12" md="2">
				<v-select v-model="filters.abc" :items="abcItems" label="Classe ABC" outlined dense hide-details />
			</v-col>
			<v-col v-if="view === 'dead'" cols="12" md="2">
				<v-select v-model="filters.dead" :items="deadItems" label="Senza vendite da" outlined dense hide-details />
			</v-col>
		</v-row>

		<v-data-table
			:headers="headers"
			:items="items"
			:options.sync="options"
			:server-items-length="total"
			:loading="loading"
			must-sort
			dense
			class="elevation-0 mt-2"
			:footer-props="{ 'items-per-page-options': [25, 50, 100] }"
			item-key="key"
			no-data-text="Nessun prodotto"
		>
			<template v-slot:[`item.title`]="{ item }">
				<div class="it-title" :title="item.title">{{ item.title || '—' }}</div>
				<div class="it-muted it-line" :title="[item.sku, item.supplierCode, item.supplierName].filter(Boolean).join(' · ')">
					{{ item.sku }}<span v-if="item.supplierCode"> · cod. {{ item.supplierCode }}</span
					><span v-if="item.supplierName"> · {{ item.supplierName }}</span>
				</div>
			</template>
			<template v-slot:[`item.stock`]="{ item }">
				<span :class="{ 'red--text': item.stock <= 0 }">{{ num(item.stock) }}</span> <span class="it-muted">{{ item.unitLabel }}</span>
			</template>
			<template v-slot:[`item.available`]="{ item }">
				<span :class="{ 'red--text': item.available <= 0 }">{{ num(item.available) }}</span>
				<div v-if="item.reserved" class="it-muted">{{ num(item.reserved) }} impegnati</div>
			</template>
			<template v-slot:[`item.sold90`]="{ item }">
				{{ num(item.sold90) }}
				<div class="it-muted">{{ num(item.sold30) }} in 30 gg</div>
			</template>
			<template v-slot:[`item.index`]="{ item }">
				<span :class="indexClass(item.index)">×{{ String(item.index).replace('.', ',') }}</span>
				<div class="it-muted">{{ item.indexSource === 'nessuna' ? 'poca storia' : `da ${item.indexSource}` }}</div>
			</template>
			<template v-slot:[`item.qty`]="{ item }">
				<div class="d-flex align-center justify-end" style="gap: 4px">
					<v-text-field
						:value="item.qty"
						type="number"
						min="0"
						dense
						hide-details
						single-line
						class="it-qty"
						:class="{ 'it-override': item.override !== null }"
						:loading="saving === item.key"
						@change="v => $emit('override', item, v)"
					/>
					<v-btn v-if="item.override !== null" icon x-small title="Torna alla proposta" @click="$emit('override', item, null)"><v-icon x-small>mdi-undo</v-icon></v-btn>
				</div>
				<div class="it-muted text-right">
					proposta {{ num(item.suggested) }}<span v-if="item.packageSize > 1"> · conf. da {{ item.packageSize }}</span>
				</div>
			</template>
			<template v-slot:[`item.qtyCost`]="{ item }">{{ formatEurCents(item.qtyCost) }}</template>
			<template v-slot:[`item.coverDays`]="{ item }">
				<span v-if="item.coverDays === null" class="it-muted">non vende</span>
				<span v-else-if="item.coverDays <= 0" class="red--text">esaurito</span>
				<span v-else :class="{ 'red--text': item.coverDays < item.leadDays }">{{ num(item.coverDays) }} gg</span>
			</template>
			<template v-slot:[`item.sellThrough`]="{ item }">{{ item.sellThrough === null ? '—' : formatPct(item.sellThrough) }}</template>
			<template v-slot:[`item.stockValue`]="{ item }">{{ formatEurCents(item.stockValue) }}</template>
			<template v-slot:[`item.lastSaleDays`]="{ item }">{{ item.lastSaleDays === null ? 'mai' : `${item.lastSaleDays} giorni fa` }}</template>
			<template v-slot:[`item.abc`]="{ item }">
				<v-chip x-small label outlined :color="item.abc === 'A' ? 'green darken-1' : item.abc === 'B' ? 'primary' : 'grey'">{{ item.abc }}</v-chip>
			</template>
		</v-data-table>
	</div>
</template>

<script>
import { formatEurCents, formatPct } from '../format';

const nf = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1 });

export default {
	name: 'InventoryTable',
	props: {
		view: { type: String, required: true },
		items: { type: Array, default: () => [] },
		total: { type: Number, default: 0 },
		loading: { type: Boolean, default: false },
		saving: { type: String, default: '' },
		suppliers: { type: Array, default: () => [] },
		value: { type: Object, required: true },
	},
	data() {
		return {
			searchfield: this.value.search || '',
			abcItems: [
				{ value: '', text: 'Tutte' },
				{ value: 'A', text: 'A · 80% del fatturato' },
				{ value: 'B', text: 'B · fino al 95%' },
				{ value: 'C', text: 'C · il resto' },
			],
			deadItems: [
				{ value: '', text: '90 o 180 giorni' },
				{ value: '90', text: '90-180 giorni' },
				{ value: '180', text: 'oltre 180 giorni o mai' },
			],
		};
	},
	computed: {
		filters() {
			return this.value;
		},
		options: {
			get() {
				return this.value.options;
			},
			set(v) {
				if (JSON.stringify(v) !== JSON.stringify(this.value.options)) this.$emit('input', { ...this.value, options: v });
			},
		},
		supplierItems() {
			return [{ value: 'none', text: 'Senza fornitore' }, ...this.suppliers.map(s => ({ value: s._id, text: s.Company || '—' }))];
		},
		headers() {
			const first = [{ text: 'Prodotto', value: 'title' }];
			const stock = [
				{ text: 'Giacenza', value: 'stock', align: 'end' },
				{ text: 'Disponibile', value: 'available', align: 'end' },
				{ text: 'Venduti 90 gg', value: 'sold90', align: 'end' },
			];
			if (this.view === 'reorder') {
				return [
					...first,
					...stock,
					{ text: 'Stagionalità', value: 'index', align: 'end', sortable: false },
					{ text: 'Copertura', value: 'coverDays', align: 'end' },
					{ text: 'Da ordinare', value: 'qty', align: 'end', sortable: false, width: 150 },
					{ text: 'Costo', value: 'qtyCost', align: 'end', sortable: false },
				];
			}
			if (this.view === 'dead') {
				return [
					...first,
					{ text: 'Giacenza', value: 'stock', align: 'end' },
					{ text: 'Valore a costo', value: 'stockValue', align: 'end' },
					{ text: 'Ultima vendita', value: 'lastSaleDays', align: 'end' },
					{ text: 'Classe', value: 'abc', sortable: false },
				];
			}
			return [
				...first,
				...stock,
				{ text: 'Copertura', value: 'coverDays', align: 'end' },
				{ text: 'Sell-through 90 gg', value: 'sellThrough', align: 'end' },
				{ text: 'Valore a costo', value: 'stockValue', align: 'end' },
				{ text: 'Classe', value: 'abc', sortable: false },
			];
		},
	},
	watch: {
		'value.supplier'() {
			this.restart();
		},
		'value.abc'() {
			this.restart();
		},
		'value.dead'() {
			this.restart();
		},
	},
	methods: {
		formatEurCents,
		formatPct,
		num: v => nf.format(+v || 0),
		indexClass(v) {
			return v > 1.15 ? 'green--text text--darken-1' : v < 0.85 ? 'orange--text text--darken-2' : '';
		},
		restart() {
			this.$emit('input', { ...this.value, options: { ...this.value.options, page: 1 } });
		},
		cerca() {
			const search = String(this.searchfield || '').length >= 3 ? this.searchfield : '';
			this.$emit('input', { ...this.value, search, options: { ...this.value.options, page: 1 } });
		},
		pulisci() {
			this.searchfield = '';
			this.cerca();
		},
	},
};
</script>

<style scoped>
.it-title {
	max-width: 420px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.it-muted {
	font-size: 12px;
	color: #5f6b7a;
}
.it-line {
	max-width: 420px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
.it-qty {
	max-width: 90px;
}
.it-qty ::v-deep input {
	text-align: right;
}
.it-override ::v-deep input {
	font-weight: 600;
	color: #1565c0;
}
</style>
